import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { db, storage, functions } from "../firebase";
import {
  collection, query, orderBy, onSnapshot, doc, addDoc, updateDoc,
  serverTimestamp, increment, Timestamp, writeBatch, runTransaction,
  limit, startAfter, getDocs, where, getCountFromServer
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import {
  ArrowUp2, ArrowDown2, ArrowLeft, SearchNormal1, Microphone2, More, InfoCircle,
  Paperclip2, EmojiHappy, Send2, DocumentText, Location, Call, Sms, Clock,
  Messages2, Stop, Trash,
} from "iconsax-reactjs";
import { isPresenceOnline } from "../utils/presence";
import { mediaLabelFor, formatFileSize, buildReplyPayload, SOFT_DELETE_FIELDS, firstUrlIn, renderTextWithLinks } from "../utils/chatShared";
import { useWebVoiceRecorder } from "../utils/useWebVoiceRecorder";
import LinkPreviewCard from "./chat/LinkPreviewCard";
import MediaCaptionModal from "./chat/MediaCaptionModal";
import EmojiPicker from "emoji-picker-react";
import "./ChatPanel.css";

const draftKey = (chatId) => `chat_draft_${chatId}`;

const sendChatNotification = httpsCallable(functions, "sendChatNotification");
// Live page size for the open conversation: only the newest N messages are
// subscribed in realtime; older history loads on demand via "Load earlier
// messages" so opening a long-running thread doesn't pull its entire history.
const MESSAGES_PAGE_SIZE = 40;

function formatTime(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDateLabel(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function getDateKey(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toDateString();
}

function formatDuration(millis) {
  const total = Math.floor((millis || 0) / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

// The composer textarea grows with its content up to this height, then scrolls.
const COMPOSER_MAX_HEIGHT = 160;

function autoGrow(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
}

// emoji-picker-react ships its own light and dark palettes; pick the one that
// matches the portal: the explicit toggle on <html> first, then the OS.
function resolvedTheme() {
  const set = document.documentElement.getAttribute("data-theme");
  if (set === "dark" || set === "light") return set;
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function ReadTicks() {
  return (
    <span className="chat-ticks">
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M2 13l4 4L16 7" />
        <path d="M11 16l1 1L22 7" />
      </svg>
      <span className="sr-only">Read</span>
    </span>
  );
}

// Renders a message body with all the types the mobile app can send:
// deleted placeholders, reply quotes, photo albums, single photo/video,
// voice notes, documents, link previews, and captions alongside media.
function MessageContent({ msg, mine }) {
  if (msg.deleted) {
    return <p className="chat-deleted">This message was deleted</p>;
  }
  const bubbleUrl = !msg.mediaUrl && !(msg.mediaUrls && msg.mediaUrls.length) ? firstUrlIn(msg.text) : null;
  return (
    <>
      {msg.replyTo && (
        <div className="chat-quote">
          <span className="chat-quote-name">{msg.replyTo.senderName || "Reply"}</span>
          <span className="chat-quote-text">{msg.replyTo.text || mediaLabelFor(msg.replyTo.mediaType)}</span>
        </div>
      )}
      {msg.mediaUrls && msg.mediaUrls.length > 1 ? (
        <div className="chat-album">
          {msg.mediaUrls.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="chat-album-tile">
              <img src={url} alt={`Attachment ${i + 1}`} />
            </a>
          ))}
        </div>
      ) : msg.mediaUrl ? (
        msg.mediaType === "image" ? (
          <a href={msg.mediaUrl} target="_blank" rel="noreferrer" className="chat-media-link"><img src={msg.mediaUrl} alt="Attachment" className="chat-media-img" /></a>
        ) : msg.mediaType === "video" ? (
          <video src={msg.mediaUrl} controls className="chat-media-video" />
        ) : msg.mediaType === "audio" ? (
          <audio src={msg.mediaUrl} controls className="chat-media-audio" />
        ) : (
          <a href={msg.mediaUrl} target="_blank" rel="noreferrer" className="chat-doc">
            <span className="chat-doc-icon"><DocumentText size={20} variant="Linear" /></span>
            <span className="chat-doc-info">
              <span className="chat-doc-name">{msg.mediaName || "Document"}</span>
              {msg.fileSize ? <span className="chat-doc-size">{formatFileSize(msg.fileSize)}</span> : null}
            </span>
          </a>
        )
      ) : null}
      {bubbleUrl ? <LinkPreviewCard url={bubbleUrl} mine={mine} /> : null}
      {(msg.isPropertyCard || msg.propertyData) && msg.propertyData ? (
        <div className="chat-property">
          {msg.propertyData.image ? (
            <div className="chat-property-media">
              <img src={msg.propertyData.image} alt={msg.propertyData.title || "Property"} />
              {msg.propertyData.price ? (
                <span className="chat-property-price">
                  ₹{Number(msg.propertyData.price).toLocaleString()}
                </span>
              ) : null}
            </div>
          ) : null}
          <div className="chat-property-body">
            <h5 className="chat-property-title">
              {msg.propertyData.title || "Property inquiry"}
            </h5>
            {msg.propertyData.location ? (
              <p className="chat-property-loc">
                <Location size={12} variant="Bold" />
                {msg.propertyData.location}
              </p>
            ) : null}
            <span className="chip chip-primary">Property inquiry</span>
          </div>
        </div>
      ) : null}
      {msg.text ? <p className="chat-text">{renderTextWithLinks(msg.text)}</p> : null}
    </>
  );
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function ChatPanel() {
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  // Live page (newest MESSAGES_PAGE_SIZE, realtime) + older pages fetched on
  // demand. Both kept oldest-first; olderMessages always precedes liveMessages.
  const [liveMessages, setLiveMessages] = useState([]);
  const [olderMessages, setOlderMessages] = useState([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesPaneRef = useRef(null);
  // Scroll bookkeeping: only auto-scroll for genuinely new messages (see the
  // scroll effect below), and only when the reader is already near the bottom.
  const isNearBottomRef = useRef(true);
  const prevNewestIdRef = useRef(null);
  // Ids already submitted for read-marking, so re-runs of the marking effect
  // (focus flips, snapshots racing the write) can't double-decrement the counter.
  const markedReadIdsRef = useRef(new Set());
  // Which chat the current liveMessages belong to. On an A→B switch, activeChat
  // flips to B before B's first snapshot replaces liveMessages, so the marking
  // effect must not act on A's stale docs against B's collection.
  const liveChatIdRef = useRef(null);
  // Which chat has already had its full-conversation backlog swept (once per open).
  const sweptChatIdRef = useRef(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const active = chats.find(c => c.id === activeChat?.id) || activeChat;
  const [isWindowFocused, setIsWindowFocused] = useState(document.hasFocus());

  // ── Parity features (reply / delete / actions / search / voice / jump) ──
  const [replyingTo, setReplyingTo] = useState(null);
  const [menuMsgId, setMenuMsgId] = useState(null);
  const [msgSearch, setMsgSearch] = useState(false);
  const [msgSearchQuery, setMsgSearchQuery] = useState("");
  const [highlightMsgId, setHighlightMsgId] = useState(null);
  const [showJumpBtn, setShowJumpBtn] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const voice = useWebVoiceRecorder();
  const [isSendingVoice, setIsSendingVoice] = useState(false);
  const [pendingMedia, setPendingMedia] = useState(null); // File[] awaiting a caption
  const [pendingVoice, setPendingVoice] = useState(null); // { blob, url, durationMillis, ext } awaiting send
  const [drafts, setDrafts] = useState({}); // chatId -> unsent text (sidebar preview)
  const messageRefs = useRef({});
  const highlightTimerRef = useRef(null);
  // Presentation only: skeletons until the first snapshot of each listener lands.
  const [chatsLoading, setChatsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const composerRef = useRef(null);

  const messages = olderMessages.length > 0 ? [...olderMessages, ...liveMessages] : liveMessages;

  // Grow the composer with its text (and again when it remounts after voice).
  useLayoutEffect(() => {
    autoGrow(composerRef.current);
  }, [replyText, voice.isRecording, pendingVoice, activeChat]);

  // Load persisted unsent drafts once (shown as "Draft: …" in the list).
  useEffect(() => {
    const map = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("chat_draft_")) {
        const val = localStorage.getItem(key);
        if (val) map[key.slice("chat_draft_".length)] = val;
      }
    }
    setDrafts(map);
  }, []);

  // Close any open per-message actions menu when clicking elsewhere.
  useEffect(() => {
    if (!menuMsgId) return;
    const close = () => setMenuMsgId(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuMsgId]);

  // Reset reply/search/jump state and restore the per-conversation draft when
  // switching conversations.
  useEffect(() => {
    setReplyingTo(null);
    setMenuMsgId(null);
    setMsgSearch(false);
    setMsgSearchQuery("");
    setShowJumpBtn(false);
    setUnseenCount(0);
    setPendingMedia(null);
    setPendingVoice((pv) => { if (pv?.url) URL.revokeObjectURL(pv.url); return null; });
    setReplyText(activeChat ? (localStorage.getItem(draftKey(activeChat.id)) || "") : "");
  }, [activeChat]);

  const saveDraft = (chatId, text) => {
    if (text.trim()) {
      localStorage.setItem(draftKey(chatId), text);
      setDrafts((d) => ({ ...d, [chatId]: text }));
    } else {
      localStorage.removeItem(draftKey(chatId));
      setDrafts((d) => { const n = { ...d }; delete n[chatId]; return n; });
    }
  };

  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);
    const handleVis = () => setIsWindowFocused(document.visibilityState === "visible" && document.hasFocus());
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVis);
    handleVis();
    return () => { window.removeEventListener("focus", handleFocus); window.removeEventListener("blur", handleBlur); document.removeEventListener("visibilitychange", handleVis); };
  }, []);

  // The mobile app reads typing state from the shared `typing` map
  // (typing.admin for support chats). Keep writing the legacy adminTyping
  // flag too so older app builds still work.
  const setAdminTypingStatus = async (isTyping) => {
    if (!activeChat) return;
    try { await updateDoc(doc(db, "chats", activeChat.id), { adminTyping: isTyping, "typing.admin": isTyping }); }
    catch (err) { console.error("Typing status error:", err); }
  };

  // Customer typing: the app writes typing.{customerUid}; legacy field kept
  // as fallback. For support chats the customer uid IS the chat id.
  const isCustomerTyping = (chat) => !!(chat?.typing?.[chat.id] || chat?.customerTyping);

  const handleTextChange = (e) => {
    const val = e.target.value;
    setReplyText(val);
    if (activeChat) {
      saveDraft(activeChat.id, val);
      if (!isTypingRef.current) { isTypingRef.current = true; setAdminTypingStatus(true); }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => { isTypingRef.current = false; setAdminTypingStatus(false); }, 3000);
    }
  };

  // Promise wrapper around a single resumable upload, driving the progress bar.
  const uploadBlob = (chatId, blob, fileName) => new Promise((resolve, reject) => {
    const storageRef = ref(storage, `chats/${chatId}/media/admin_${Date.now()}_${fileName}`);
    const task = uploadBytesResumable(storageRef, blob);
    task.on("state_changed",
      (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => { try { resolve(await getDownloadURL(task.snapshot.ref)); } catch (err) { reject(err); } });
  });

  const bumpConversation = async (chatId, label) => {
    await updateDoc(doc(db, "chats", chatId), { lastMessage: label, lastMessageTime: serverTimestamp(), lastSenderId: "admin", updatedAt: serverTimestamp(), unreadCount: increment(1) });
    sendChatNotification({ recipientId: chatId, conversationId: chatId, senderName: "GharSetu Support", body: label }).catch(() => {});
  };

  // Picking files opens the caption composer instead of sending immediately.
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = "";
    if (files.length === 0 || !activeChat) return;
    setPendingMedia(files);
  };

  const uploadPendingMedia = async (files, caption) => {
    if (!activeChat || !files || files.length === 0) return;
    const chatId = activeChat.id;
    const images = files.filter((f) => f.type.startsWith("image/"));
    const others = files.filter((f) => !f.type.startsWith("image/"));
    let captionLeft = caption || ""; // caption goes on the first message only

    setIsUploading(true); setUploadProgress(0);
    try {
      // Multiple images → one captioned album message (mediaUrls), matching mobile.
      if (images.length > 1) {
        const urls = [];
        for (const f of images) urls.push(await uploadBlob(chatId, f, f.name));
        await addDoc(collection(db, "chats", chatId, "messages"), {
          text: captionLeft, sender: "admin", senderId: "admin", time: Timestamp.now(), isRead: false,
          mediaUrl: urls[0], mediaUrls: urls, mediaType: "image", mediaName: images[0].name,
        });
        await bumpConversation(chatId, captionLeft || `📷 ${urls.length} Photos`);
        captionLeft = "";
      } else if (images.length === 1) {
        others.unshift(images[0]); // single image sent like any other attachment
      }
      for (const file of others) {
        const mediaType = file.type.startsWith("image/") ? "image"
          : file.type.startsWith("video/") ? "video"
          : file.type.startsWith("audio/") ? "audio"
          : "document";
        const url = await uploadBlob(chatId, file, file.name);
        const thisCaption = captionLeft; captionLeft = "";
        await addDoc(collection(db, "chats", chatId, "messages"), {
          text: thisCaption, sender: "admin", senderId: "admin", time: Timestamp.now(), isRead: false,
          mediaUrl: url, mediaType, mediaName: file.name, fileSize: file.size,
        });
        await bumpConversation(chatId, thisCaption || mediaLabelFor(mediaType));
      }
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed!");
    } finally {
      setIsUploading(false); setUploadProgress(0); setPendingMedia(null);
    }
  };

  // ── Voice notes: record → preview → send (mirrors mobile) ──────────
  const handleStopRecording = async () => {
    const result = await voice.stop();
    if (!result || result.durationMillis < 800) return; // ignore accidental taps
    setPendingVoice({ ...result, url: URL.createObjectURL(result.blob) });
  };

  const discardPendingVoice = () => {
    setPendingVoice((pv) => { if (pv?.url) URL.revokeObjectURL(pv.url); return null; });
  };

  const handleSendPendingVoice = async () => {
    if (!activeChat || !pendingVoice) return;
    const chatId = activeChat.id;
    const clip = pendingVoice;
    setIsSendingVoice(true);
    try {
      const fileName = `voice_${chatId}_${Date.now()}.${clip.ext}`;
      const url = await uploadBlob(chatId, clip.blob, fileName);
      await addDoc(collection(db, "chats", chatId, "messages"), {
        text: "", sender: "admin", senderId: "admin", time: Timestamp.now(), isRead: false,
        mediaUrl: url, mediaType: "audio", mediaName: fileName, durationMillis: clip.durationMillis,
      });
      await bumpConversation(chatId, "🎤 Voice message");
      if (clip.url) URL.revokeObjectURL(clip.url);
      setPendingVoice(null);
    } catch (err) {
      console.error("Voice send failed:", err);
    } finally {
      setIsSendingVoice(false);
    }
  };

  // ── Per-message actions: reply / copy / delete ─────────────────────
  const startReply = (msg) => { setReplyingTo(msg); setMenuMsgId(null); };
  const handleCopy = (msg) => { if (msg.text && navigator.clipboard) navigator.clipboard.writeText(msg.text).catch(() => {}); setMenuMsgId(null); };
  const handleDeleteMessage = async (msg) => {
    setMenuMsgId(null);
    if (!activeChat || !window.confirm("Delete this message for everyone?")) return;
    try {
      await updateDoc(doc(db, "chats", activeChat.id, "messages", msg.id), SOFT_DELETE_FIELDS);
    } catch (err) { console.error("Delete message failed:", err); }
  };

  // Append the picked emoji to the composer (and its draft); the full picker
  // stays open so several can be added in a row, closing on outside click.
  const handleEmojiSelect = (emoji) => {
    setReplyText((prev) => {
      const next = prev + emoji;
      if (activeChat) saveDraft(activeChat.id, next);
      return next;
    });
  };

  // Close the emoji picker on any click outside it.
  useEffect(() => {
    if (!showEmojiPicker) return;
    const close = () => setShowEmojiPicker(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [showEmojiPicker]);

  useEffect(() => {
    const q = query(collection(db, "chats"), orderBy("updatedAt", "desc"));
    return onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => { if (!d.id.includes("_")) list.push({ id: d.id, ...d.data() }); });
      list.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
      setChats(list);
      setChatsLoading(false);
    }, (err) => { console.error("Chats error:", err); setChatsLoading(false); });
  }, []);

  // Fetch the live (newest MESSAGES_PAGE_SIZE) page for the active chat.
  // Older history is loaded on demand via loadOlderMessages, not upfront:
  // otherwise a long-running support thread reloads its entire history every
  // time it's opened. Keyed on activeChat ONLY: read-marking lives in its own
  // effect below, so window focus changes don't tear down the listener and
  // wipe paged-in history.
  useEffect(() => {
    setLiveMessages([]); // clear stale messages so the switched-to chat never shows the old thread
    setOlderMessages([]);
    setHasMoreOlder(true);
    isNearBottomRef.current = true;
    prevNewestIdRef.current = null;
    markedReadIdsRef.current = new Set();
    sweptChatIdRef.current = null;
    setMessagesLoading(!!activeChat);

    if (!activeChat) { return; }
    const q = query(collection(db, "chats", activeChat.id, "messages"), orderBy("time", "desc"), limit(MESSAGES_PAGE_SIZE));
    return onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.reverse(); // desc (newest-first) query → ascending for display
      liveChatIdRef.current = activeChat.id;
      setLiveMessages(list);
      setMessagesLoading(false);
      if (list.length < MESSAGES_PAGE_SIZE) setHasMoreOlder(false);
    }, (err) => { console.error("Messages error:", err); setMessagesLoading(false); });
  }, [activeChat]);

  // Mark the customer's live-page messages read while the window is focused.
  // Separate from the subscription so focus flips only re-run this cheap scan;
  // markedReadIdsRef keeps re-runs from re-submitting (and re-decrementing) ids
  // whose writes haven't reflected in a snapshot yet. The liveChatIdRef guard
  // stops a stale liveMessages array (from the previous chat) being marked
  // against the just-switched-to chat's collection.
  useEffect(() => {
    if (!activeChat || !isWindowFocused || liveChatIdRef.current !== activeChat.id) return;
    const unreadIds = liveMessages
      .filter((m) => m.sender === "customer" && !(m.status === "read" && m.isRead) && !markedReadIdsRef.current.has(m.id))
      .map((m) => m.id);
    if (unreadIds.length === 0) return;
    unreadIds.forEach((id) => markedReadIdsRef.current.add(id));
    markMessagesRead(activeChat.id, unreadIds).catch((err) => console.error("Batch read update failed:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChat, isWindowFocused, liveMessages]);

  // One-shot sweep for unread customer messages beyond the live page: the
  // per-snapshot marking above only sees the newest MESSAGES_PAGE_SIZE, so a
  // larger unread backlog would leave the older messages (and their share of
  // adminUnreadCount) stuck. Runs once per chat, the first time it's focused.
  useEffect(() => {
    if (!activeChat || !isWindowFocused || sweptChatIdRef.current === activeChat.id) return;
    const chatId = activeChat.id;
    sweptChatIdRef.current = chatId;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, "chats", chatId, "messages"),
          where("isRead", "==", false),
          limit(300),
        ));
        const ids = [];
        snap.forEach((d) => {
          if (d.data().sender === "customer" && !markedReadIdsRef.current.has(d.id)) ids.push(d.id);
        });
        if (cancelled || ids.length === 0) return;
        ids.forEach((id) => markedReadIdsRef.current.add(id));
        await markMessagesRead(chatId, ids);
      } catch (err) {
        if (!cancelled) { sweptChatIdRef.current = null; console.error("Unread backlog sweep failed:", err); }
      }
    })();
    return () => { cancelled = true; };
  }, [activeChat, isWindowFocused]);

  // Load the next page of older history, preserving scroll position (the
  // pane would otherwise jump to the top as content is prepended above it).
  const loadOlderMessages = async () => {
    if (!activeChat || loadingOlder || !hasMoreOlder || messages.length === 0) return;
    setLoadingOlder(true);
    const pane = messagesPaneRef.current;
    const prevScrollHeight = pane ? pane.scrollHeight : 0;
    try {
      const oldest = messages[0];
      const q = query(
        collection(db, "chats", activeChat.id, "messages"),
        orderBy("time", "desc"),
        startAfter(oldest.time),
        limit(MESSAGES_PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const older = [];
      snap.forEach((d) => older.push({ id: d.id, ...d.data() }));
      if (older.length < MESSAGES_PAGE_SIZE) setHasMoreOlder(false);
      older.reverse();
      if (older.length > 0) {
        setOlderMessages((prev) => [...older, ...prev]);
        requestAnimationFrame(() => {
          if (pane) pane.scrollTop = pane.scrollHeight - prevScrollHeight;
        });
      }
    } catch (err) {
      console.error("Failed to load older messages:", err);
    } finally {
      setLoadingOlder(false);
    }
  };

  // Mark a batch of customer messages read and decrement (never hard-reset)
  // the admin's own unread counter by however many were just marked: the
  // customer's own "new message" bump is a separate, unordered increment(1)
  // write to the same field, so a hard reset can lose a race and land after
  // it, stranding the badge at a stale non-zero count. A transaction re-reads
  // the live value so the decrement always applies on top of whatever the
  // customer last wrote, however the two writes are interleaved.
  const markMessagesRead = async (convId, unreadIds) => {
    // isRead is what the mobile app renders as blue ticks; status is the
    // legacy web-side field. Write both so receipts sync everywhere.
    const batch = writeBatch(db);
    unreadIds.forEach((msgId) => {
      batch.update(doc(db, "chats", convId, "messages", msgId), { status: "read", isRead: true });
    });
    await batch.commit();

    const chatDocRef = doc(db, "chats", convId);
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(chatDocRef);
      const data = snap.data();
      if (!data) return;
      const current = data.adminUnreadCount || 0;
      tx.update(chatDocRef, { adminUnreadCount: Math.max(0, current - unreadIds.length) });
    });
  };

  // Auto-scroll only when a genuinely NEW message arrives (read-tick and other
  // in-place edits keep the same newest id, so they never move the view), and
  // only if the reader is already near the bottom, or the message is our own.
  useEffect(() => {
    const newest = liveMessages[liveMessages.length - 1];
    if (!newest) { prevNewestIdRef.current = null; return; }
    if (newest.id === prevNewestIdRef.current) return;
    prevNewestIdRef.current = newest.id;
    const isMine = (newest.senderId || newest.sender) === "admin";
    if (isMine || isNearBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      // Reader is scrolled up in history: surface the new message on the pill.
      setUnseenCount((c) => c + 1);
    }
  }, [liveMessages]);

  const handlePaneScroll = () => {
    const pane = messagesPaneRef.current;
    if (!pane) return;
    const distanceFromBottom = pane.scrollHeight - pane.scrollTop - pane.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 120;
    setShowJumpBtn(distanceFromBottom > 300);
    if (distanceFromBottom < 120) setUnseenCount(0);
  };

  const jumpToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setUnseenCount(0);
    setShowJumpBtn(false);
  };

  // Scroll a specific message into view + briefly highlight it (search jump).
  const jumpToMessage = (id) => {
    setMsgSearch(false);
    setMsgSearchQuery("");
    const el = messageRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightMsgId(id);
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    highlightTimerRef.current = setTimeout(() => setHighlightMsgId(null), 1800);
  };

  // True whole-conversation totals for the info panel, via cheap server-side
  // aggregates: with pagination, messages.length only counts loaded pages.
  // Deferred until the info panel is actually opened so we don't bill two count
  // reads on every chat selection for a stat that's rarely viewed.
  const [chatTotals, setChatTotals] = useState(null);
  useEffect(() => {
    if (!activeChat || !showInfoPanel) { setChatTotals(null); return; }
    let cancelled = false;
    const msgsCol = collection(db, "chats", activeChat.id, "messages");
    Promise.all([
      getCountFromServer(msgsCol),
      // mediaUrl != null skips both text messages (explicit null) and legacy
      // docs missing the field entirely.
      getCountFromServer(query(msgsCol, where("mediaUrl", "!=", null))),
    ])
      .then(([all, media]) => { if (!cancelled) setChatTotals({ messages: all.data().count, media: media.data().count }); })
      .catch((err) => {
        // Surfaced (not swallowed) so a failing aggregate (e.g. a missing index
        // for the mediaUrl inequality) is visible rather than silently showing
        // the "N+" fallback forever.
        if (!cancelled) { setChatTotals(null); console.error("Chat stats aggregate failed:", err); }
      });
    return () => { cancelled = true; };
  }, [activeChat, showInfoPanel]);

  // Live customer presence from users/{uid} (written by the app's
  // usePresenceReporter): online flag + lastSeen timestamp.
  const [customerPresence, setCustomerPresence] = useState(null);
  useEffect(() => {
    if (!activeChat) { setCustomerPresence(null); return; }
    return onSnapshot(doc(db, "users", activeChat.id), (snap) => {
      setCustomerPresence(snap.exists() ? snap.data() : null);
    }, () => setCustomerPresence(null));
  }, [activeChat]);

  // Real presence from the live users doc with a staleness guard, NOT the chat
  // doc's `status`, which is hardcoded "online" at support-chat creation and
  // never updated (it would pin the header to "Active now" forever).
  const customerOnline = isPresenceOnline(customerPresence?.online, customerPresence?.lastSeen);
  const customerLastSeenLabel = (() => {
    if (customerOnline) return "Active now";
    const ls = customerPresence?.lastSeen;
    if (!ls?.toDate) return "Offline";
    const d = ls.toDate();
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return sameDay ? `last seen today at ${time}` : `last seen ${d.toLocaleDateString([], { month: "short", day: "numeric" })}`;
  })();

  useEffect(() => {
    if (!activeChat) return;
    const chatRef = doc(db, "chats", activeChat.id);
    const updatePresence = (online) => updateDoc(chatRef, { adminStatus: online ? "online" : "offline" }).catch(() => {});
    updatePresence(isWindowFocused);
    return () => { updateDoc(chatRef, { adminStatus: "offline", adminTyping: false }).catch(() => {}); };
  }, [activeChat, isWindowFocused]);

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(e); } };

  const handleSend = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!replyText.trim() || !activeChat) return;
    const text = replyText.trim(); setReplyText("");
    saveDraft(activeChat.id, "");
    const replyPayload = replyingTo ? buildReplyPayload(replyingTo, "admin", active?.customerName || "Customer") : null;
    setReplyingTo(null);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    isTypingRef.current = false; setAdminTypingStatus(false);
    try {
      await addDoc(collection(db, "chats", activeChat.id, "messages"), { text, sender: "admin", senderId: "admin", time: Timestamp.now(), isRead: false, replyTo: replyPayload });
      await updateDoc(doc(db, "chats", activeChat.id), { lastMessage: text, lastMessageTime: serverTimestamp(), lastSenderId: "admin", updatedAt: serverTimestamp(), unreadCount: increment(1) });
      sendChatNotification({ recipientId: activeChat.id, conversationId: activeChat.id, senderName: "GharSetu Support", body: text }).catch(() => {});
    } catch (err) { console.error("Send error:", err); }
  };

  const filteredChats = chats.filter(c =>
    !searchQuery || c.customerName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // In-conversation message search over loaded messages (matches the mobile
  // in-thread search, which likewise searches the loaded history).
  const searchResults = msgSearchQuery.trim()
    ? messages.filter((m) => !m.deleted && m.text && m.text.toLowerCase().includes(msgSearchQuery.trim().toLowerCase()))
    : [];

  // Date separators + a precomputed "is this the last bubble in a run from
  // the same sender" flag (avoids an O(n²) findIndex per row during render).
  const messagesWithDates = [];
  let lastDateKey = "";
  messages.forEach((msg, idx) => {
    const dk = getDateKey(msg.time);
    if (dk && dk !== lastDateKey) { messagesWithDates.push({ type: "date", label: formatDateLabel(msg.time), key: `date-${dk}` }); lastDateKey = dk; }
    const next = messages[idx + 1];
    const isLastInSequence = idx === messages.length - 1 || next?.sender !== msg.sender || getDateKey(next?.time) !== dk;
    messagesWithDates.push({ type: "msg", ...msg, isLastInSequence });
  });

  const customerName = active?.customerName || "Customer";
  const avatarFor = (chat, className) => (
    <span className={`avatar ${className}`} aria-hidden="true">
      {chat.customerAvatar ? <img src={chat.customerAvatar} alt="" /> : getInitials(chat.customerName)}
    </span>
  );

  return (
    <div className={`chat-shell ${active ? "is-thread-open" : ""}`}>
      {/* Conversation list */}
      <section className="chat-list" aria-label="Conversations">
        <div className="chat-list-head">
          <div className="search-field">
            <SearchNormal1 size={16} variant="Linear" />
            <input
              type="text"
              className="input"
              placeholder="Search conversations"
              aria-label="Search conversations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {!chatsLoading && chats.length > 0 && (
            <span className="chat-list-meta">
              {chats.length} {chats.length === 1 ? "conversation" : "conversations"}
            </span>
          )}
        </div>
        <div className="chat-list-body">
          {chatsLoading ? (
            <div aria-busy="true">
              <span className="sr-only" role="status">Loading conversations</span>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="chat-row-skeleton" aria-hidden="true">
                  <span className="skeleton chat-sk-avatar" />
                  <span className="chat-sk-lines">
                    <span className="skeleton chat-sk-line" />
                    <span className="skeleton chat-sk-line is-long" />
                  </span>
                </div>
              ))}
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="empty-state chat-list-empty">
              <span className="empty-state-icon"><Messages2 size={22} variant="Linear" /></span>
              {searchQuery ? (
                <>
                  <h3>No matches</h3>
                  <p>No conversations match “{searchQuery}”. Search by the customer’s name.</p>
                </>
              ) : (
                <>
                  <h3>No conversations yet</h3>
                  <p>When app users message GharSetu support, their chats appear here for you to answer.</p>
                </>
              )}
            </div>
          ) : (
            filteredChats.map((chat) => {
              const isActive = activeChat?.id === chat.id;
              const unread = chat.adminUnreadCount || 0;
              return (
                <button
                  key={chat.id}
                  type="button"
                  className={`chat-row ${isActive ? "is-active" : ""} ${unread > 0 ? "is-unread" : ""}`}
                  aria-current={isActive ? "true" : undefined}
                  onClick={() => { setActiveChat(chat); setShowInfoPanel(false); }}
                >
                  {avatarFor(chat, "chat-row-avatar")}
                  <span className="chat-row-main">
                    <span className="chat-row-top">
                      <span className="chat-row-name">{chat.customerName}</span>
                      <span className="chat-row-time">{formatTime(chat.lastMessageTime)}</span>
                    </span>
                    <span className="chat-row-bottom">
                      <span className="chat-row-preview">
                        {isCustomerTyping(chat)
                          ? <span className="chat-row-typing">Typing…</span>
                          : drafts[chat.id]
                            ? <><span className="chat-row-draft">Draft:</span> {drafts[chat.id]}</>
                            : chat.lastMessage}
                      </span>
                      {unread > 0 && (
                        <span className="count-badge" aria-label={`${unread} unread`}>{unread > 99 ? "99+" : unread}</span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </section>

      {/* Thread */}
      <section className="chat-thread" aria-label={active ? `Conversation with ${customerName}` : "Conversation"}>
        {active ? (
          <>
            <header className="chat-thread-head">
              <button type="button" className="btn-icon chat-back" onClick={() => setActiveChat(null)} aria-label="Back to conversations">
                <ArrowLeft size={20} variant="Linear" />
              </button>
              {avatarFor(active, "chat-head-avatar")}
              <div className="chat-head-text">
                <h2 className="chat-head-name">{active.customerName}</h2>
                <p className="chat-head-sub">
                  <span className={`chat-presence ${customerOnline ? "is-online" : ""}`} aria-hidden="true" />
                  {isCustomerTyping(active) ? "Typing…" : customerLastSeenLabel}
                </p>
              </div>
              <div className="chat-head-actions">
                <button
                  type="button"
                  className="btn-icon"
                  aria-pressed={msgSearch}
                  aria-label="Search in conversation"
                  title="Search in conversation"
                  onClick={() => { setMsgSearch((v) => !v); setMsgSearchQuery(""); }}
                >
                  <SearchNormal1 size={18} variant="Linear" />
                </button>
                <button
                  type="button"
                  className="btn-icon"
                  aria-pressed={showInfoPanel}
                  aria-label="Customer info"
                  title="Customer info"
                  onClick={() => setShowInfoPanel(!showInfoPanel)}
                >
                  <InfoCircle size={18} variant="Linear" />
                </button>
              </div>
            </header>

            {msgSearch && (
              <div className="chat-find">
                <div className="search-field">
                  <SearchNormal1 size={16} variant="Linear" />
                  <input
                    type="text"
                    className="input"
                    autoFocus
                    placeholder="Search in this conversation"
                    aria-label="Search in this conversation"
                    value={msgSearchQuery}
                    onChange={(e) => setMsgSearchQuery(e.target.value)}
                  />
                </div>
                <button type="button" className="btn-icon" onClick={() => { setMsgSearch(false); setMsgSearchQuery(""); }} aria-label="Close search">
                  <CloseIcon />
                </button>
              </div>
            )}
            {msgSearch && msgSearchQuery.trim() && (
              <div className="chat-find-results">
                {searchResults.length === 0 ? (
                  <p className="chat-find-empty">No messages found for “{msgSearchQuery.trim()}”.</p>
                ) : (
                  searchResults.map((m) => (
                    <button key={m.id} type="button" className="chat-find-result" onClick={() => jumpToMessage(m.id)}>
                      <span className="chat-find-who">{m.sender === "admin" ? "You" : customerName}</span>
                      <span className="chat-find-text">{m.text}</span>
                    </button>
                  ))
                )}
              </div>
            )}

            <div className="chat-thread-main">
              <div className="chat-scroll">
                <div className="chat-messages" ref={messagesPaneRef} onScroll={handlePaneScroll}>
                  {hasMoreOlder && messages.length > 0 && (
                    <div className="chat-older">
                      <button type="button" className="btn-secondary btn-sm" onClick={loadOlderMessages} disabled={loadingOlder}>
                        {loadingOlder ? <><span className="btn-spinner" /> Loading</> : <><ArrowUp2 size={14} variant="Linear" /> Load earlier messages</>}
                      </button>
                    </div>
                  )}
                  {messagesLoading ? (
                    <div className="chat-msgs-skeleton" aria-busy="true">
                      <span className="sr-only" role="status">Loading messages</span>
                      <span className="skeleton chat-sk-bubble is-theirs is-lg" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-theirs is-sm" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-mine is-md" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-theirs is-md" aria-hidden="true" />
                      <span className="skeleton chat-sk-bubble is-mine is-lg" aria-hidden="true" />
                    </div>
                  ) : messagesWithDates.length === 0 ? (
                    <div className="empty-state chat-thread-empty">
                      <span className="empty-state-icon"><Messages2 size={22} variant="Linear" /></span>
                      <h3>No messages yet</h3>
                      <p>Say hello to start the conversation.</p>
                    </div>
                  ) : (
                    messagesWithDates.map((item) => {
                      if (item.type === "date") {
                        return <div key={item.key} className="chat-day"><span>{item.label}</span></div>;
                      }
                      const msg = item;
                      const isAdmin = msg.sender === "admin";
                      const menuOpen = menuMsgId === msg.id;
                      return (
                        <div
                          key={msg.id}
                          ref={(el) => { if (el) messageRefs.current[msg.id] = el; }}
                          className={`chat-msg ${isAdmin ? "is-mine" : "is-theirs"} ${msg.isLastInSequence ? "is-tail" : ""} ${highlightMsgId === msg.id ? "is-flash" : ""}`}
                        >
                          <div className="chat-bubble">
                            <MessageContent msg={msg} mine={isAdmin} />
                            <span className="chat-meta">
                              {formatTime(msg.time)}
                              {isAdmin && (msg.status === "read" || msg.isRead) && <ReadTicks />}
                            </span>
                          </div>
                          {!msg.deleted && (
                            <div className={`chat-msg-tools ${menuOpen ? "is-open" : ""}`}>
                              <button
                                type="button"
                                className="chat-msg-more"
                                aria-label="Message actions"
                                aria-haspopup="menu"
                                aria-expanded={menuOpen}
                                onClick={(e) => { e.stopPropagation(); setMenuMsgId(menuOpen ? null : msg.id); }}
                              >
                                <More size={16} variant="Linear" />
                              </button>
                              {menuOpen && (
                                <div className="chat-menu chat-msg-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                                  <button type="button" role="menuitem" onClick={() => startReply(msg)}>Reply</button>
                                  {msg.text ? <button type="button" role="menuitem" onClick={() => handleCopy(msg)}>Copy</button> : null}
                                  {isAdmin ? <button type="button" role="menuitem" className="is-danger" onClick={() => handleDeleteMessage(msg)}>Delete</button> : null}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                  {isCustomerTyping(active) && (
                    <div className="chat-msg is-theirs is-tail">
                      <div className="chat-bubble chat-typing">
                        <span className="sr-only">{customerName} is typing</span>
                        <span className="chat-typing-dots" aria-hidden="true"><i /><i /><i /></span>
                      </div>
                    </div>
                  )}
                  {isUploading && (
                    <div className="chat-msg is-mine is-tail">
                      <div className="chat-bubble chat-uploading" role="status">
                        <p className="chat-text">Uploading {uploadProgress}%</p>
                        <span className="chat-progress" aria-hidden="true"><span style={{ width: `${uploadProgress}%` }} /></span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {showJumpBtn && (
                  <button
                    type="button"
                    className="chat-jump"
                    onClick={jumpToBottom}
                    aria-label={unseenCount > 0 ? `Jump to latest, ${unseenCount} new` : "Jump to latest"}
                  >
                    <ArrowDown2 size={18} variant="Linear" />
                    {unseenCount > 0 && <span className="count-badge chat-jump-count" aria-hidden="true">{unseenCount > 99 ? "99+" : unseenCount}</span>}
                  </button>
                )}
              </div>

              {/* Customer info */}
              {showInfoPanel && (
                <aside className="chat-info" aria-label="Customer info">
                  <div className="chat-info-head">
                    <h3>Customer info</h3>
                    <button type="button" className="btn-icon" onClick={() => setShowInfoPanel(false)} aria-label="Close customer info">
                      <CloseIcon />
                    </button>
                  </div>
                  <div className="chat-info-body">
                    <div className="chat-info-id">
                      {avatarFor(active, "chat-info-avatar")}
                      <h4>{active.customerName}</h4>
                      <span className={`chip chip-dot ${customerOnline ? "chip-success" : ""}`}>
                        {customerOnline ? "Online" : "Offline"}
                      </span>
                    </div>
                    <div className="chat-info-rows">
                      {active.customerPhone && (
                        <div className="chat-info-row">
                          <Call size={16} variant="Linear" />
                          <span>{active.customerPhone}</span>
                        </div>
                      )}
                      {active.customerEmail && (
                        <div className="chat-info-row">
                          <Sms size={16} variant="Linear" />
                          <span>{active.customerEmail}</span>
                        </div>
                      )}
                      <div className="chat-info-row">
                        <Clock size={16} variant="Linear" />
                        <span>{active.createdAt ? `Chat started ${formatDateLabel(active.createdAt)}` : "Start date unknown"}</span>
                      </div>
                    </div>
                    <div>
                      <h5 className="chat-info-label">Chat stats</h5>
                      <div className="chat-info-stats">
                        <div className="chat-info-stat">
                          <strong>{chatTotals ? chatTotals.messages : `${messages.length}${hasMoreOlder ? "+" : ""}`}</strong>
                          <span>Messages</span>
                        </div>
                        <div className="chat-info-stat">
                          <strong>{chatTotals ? chatTotals.media : `${messages.filter(m => m.mediaUrl).length}${hasMoreOlder ? "+" : ""}`}</strong>
                          <span>Media</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </aside>
              )}
            </div>

            <div className="chat-composer">
              {replyingTo && (
                <div className="chat-replying">
                  <div className="chat-replying-body">
                    <span className="chat-replying-name">
                      {(replyingTo.senderId || replyingTo.sender) === "admin" ? "You" : customerName}
                    </span>
                    <span className="chat-replying-text">{replyingTo.text || mediaLabelFor(replyingTo.mediaType)}</span>
                  </div>
                  <button type="button" className="btn-icon" onClick={() => setReplyingTo(null)} aria-label="Cancel reply">
                    <CloseIcon />
                  </button>
                </div>
              )}

              {voice.isRecording ? (
                <div className="chat-composer-bar chat-voice">
                  <button type="button" className="btn-icon chat-voice-discard" onClick={() => voice.cancel()} aria-label="Cancel recording">
                    <Trash size={18} variant="Linear" />
                  </button>
                  <span className="chat-voice-dot" aria-hidden="true" />
                  <span className="chat-voice-timer" role="timer" aria-label="Recording time">{formatDuration(voice.durationMillis)}</span>
                  <button type="button" className="chat-send" onClick={handleStopRecording} aria-label="Stop recording">
                    <Stop size={16} variant="Bold" />
                  </button>
                </div>
              ) : pendingVoice ? (
                <div className="chat-composer-bar chat-voice">
                  <button type="button" className="btn-icon chat-voice-discard" onClick={discardPendingVoice} disabled={isSendingVoice} aria-label="Discard recording">
                    <Trash size={18} variant="Linear" />
                  </button>
                  <audio src={pendingVoice.url} controls className="chat-voice-preview" />
                  <button type="button" className="chat-send" onClick={handleSendPendingVoice} disabled={isSendingVoice} aria-label="Send voice message">
                    {isSendingVoice ? <span className="btn-spinner" /> : <Send2 size={18} variant="Bold" />}
                  </button>
                </div>
              ) : (
                <div className="chat-composer-bar">
                  <input type="file" ref={fileInputRef} onChange={handleFileSelect} hidden multiple accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
                  <button type="button" className="btn-icon" onClick={() => fileInputRef.current?.click()} disabled={isUploading} aria-label="Attach files" title="Attach files">
                    <Paperclip2 size={20} variant="Linear" />
                  </button>
                  <div className="chat-emoji" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      aria-label="Add emoji"
                      aria-expanded={showEmojiPicker}
                      title="Add emoji"
                    >
                      <EmojiHappy size={20} variant="Linear" />
                    </button>
                    {showEmojiPicker && (
                      <div className="chat-emoji-popover">
                        <EmojiPicker
                          onEmojiClick={(data) => handleEmojiSelect(data.emoji)}
                          width={320}
                          height={380}
                          theme={resolvedTheme()}
                          previewConfig={{ showPreview: false }}
                          lazyLoadEmojis
                        />
                      </div>
                    )}
                  </div>
                  <textarea
                    ref={composerRef}
                    className="chat-composer-input"
                    placeholder={isUploading ? "Uploading…" : "Type a message"}
                    aria-label="Message"
                    value={replyText}
                    onChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    disabled={isUploading}
                    rows={1}
                  />
                  {!replyText.trim() && voice.supported && (
                    <button type="button" className="btn-icon" onClick={() => voice.start()} disabled={isUploading} aria-label="Record voice message" title="Record voice message">
                      <Microphone2 size={20} variant="Linear" />
                    </button>
                  )}
                  <button type="button" onClick={handleSend} className="chat-send" disabled={isUploading || !replyText.trim()} aria-label="Send message">
                    <Send2 size={18} variant="Bold" />
                  </button>
                </div>
              )}
            </div>

            {pendingMedia && (
              <MediaCaptionModal
                files={pendingMedia}
                uploading={isUploading}
                progress={uploadProgress}
                onSend={(caption) => uploadPendingMedia(pendingMedia, caption)}
                onCancel={() => setPendingMedia(null)}
              />
            )}
          </>
        ) : (
          <div className="empty-state chat-thread-placeholder">
            <span className="empty-state-icon"><Messages2 size={24} variant="Linear" /></span>
            <h3>Select a conversation</h3>
            <p>Choose a chat from the list to read the history and start responding.</p>
          </div>
        )}
      </section>
    </div>
  );
}
